import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Search, RefreshCw, Filter, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("mv_admin_token")}` },
});

const ENTITY_LABELS = {
  shipment: "Spedizione", pickup: "Ritiro", client: "Cliente", driver: "Autista",
  distinta: "Distinta", invoicing: "Fatturazione",
  warehouse_inventory: "Magazzino · Inventario", warehouse_movement: "Magazzino · Movimento",
  warehouse_order: "Magazzino · Ordine", warehouse_request: "Magazzino · Richiesta",
  warehouse_location: "Magazzino · Posizione", warehouse: "Magazzino",
  absence: "Assenza", notification: "Notifica", governance: "Governance",
  quote: "Preventivo", admin_other: "Altro Admin", unknown: "Sconosciuto",
};
const ACTION_LABELS = {
  create: "Crea", update: "Modifica", delete: "Elimina", approve: "Approva",
  reject: "Rifiuta", assign: "Assegna", cancel: "Annulla",
};
const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
  approve: "bg-green-100 text-green-700",
  reject: "bg-orange-100 text-orange-700",
  assign: "bg-violet-100 text-violet-700",
  cancel: "bg-amber-100 text-amber-700",
};

export default function AdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [filtersData, setFiltersData] = useState({ users: [], entities: [], actions: [] });
  const [filters, setFilters] = useState({ user: "", entity: "all", action: "all", date_from: "", date_to: "" });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit);
      params.set("skip", page * limit);
      if (filters.user) params.set("user", filters.user);
      if (filters.entity !== "all") params.set("entity", filters.entity);
      if (filters.action !== "all") params.set("action", filters.action);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      const r = await axios.get(`${API}/admin/audit-logs?${params}`, authHeaders());
      setLogs(r.data.logs);
      setTotal(r.data.total);
      setFiltersData(r.data.filters);
    } catch { toast.error("Errore caricamento audit log"); }
    setLoading(false);
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const cleanup = async () => {
    if (!window.confirm("Eliminare i log piu vecchi di 90 giorni?")) return;
    try {
      const r = await axios.delete(`${API}/admin/audit-logs/cleanup?days=90`, authHeaders());
      toast.success(`Eliminati ${r.data.deleted} log`);
      load();
    } catch { toast.error("Errore pulizia"); }
  };

  const formatTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="admin-audit-log">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-wide">Audit Log</h1>
          <p className="text-sm text-zinc-500">Cronologia di tutte le modifiche amministrative</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load()} data-testid="audit-refresh-btn"
            className="flex items-center gap-2 border border-zinc-200 px-3 py-2 rounded-sm text-sm font-bold uppercase hover:bg-zinc-50">
            <RefreshCw size={14} /> Aggiorna
          </button>
          <button onClick={cleanup} data-testid="audit-cleanup-btn"
            className="flex items-center gap-2 border border-red-200 text-red-600 px-3 py-2 rounded-sm text-sm font-bold uppercase hover:bg-red-50">
            <Trash2 size={14} /> Pulisci &gt;90gg
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Utente</label>
          <input value={filters.user} onChange={e => { setFilters({ ...filters, user: e.target.value }); setPage(0); }}
            placeholder="email..." data-testid="filter-user"
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Entita</label>
          <select value={filters.entity} onChange={e => { setFilters({ ...filters, entity: e.target.value }); setPage(0); }}
            data-testid="filter-entity"
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm bg-white">
            <option value="all">Tutte</option>
            {filtersData.entities.map(e => <option key={e} value={e}>{ENTITY_LABELS[e] || e}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Azione</label>
          <select value={filters.action} onChange={e => { setFilters({ ...filters, action: e.target.value }); setPage(0); }}
            data-testid="filter-action"
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm bg-white">
            <option value="all">Tutte</option>
            {filtersData.actions.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Da data</label>
          <input type="date" value={filters.date_from} onChange={e => { setFilters({ ...filters, date_from: e.target.value }); setPage(0); }}
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">A data</label>
          <input type="date" value={filters.date_to} onChange={e => { setFilters({ ...filters, date_to: e.target.value }); setPage(0); }}
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#FF5200]" size={28} /></div>
      ) : logs.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center text-zinc-400">
          Nessun log trovato. {Object.values(filters).some(Boolean) && "Prova a rimuovere i filtri."}
        </div>
      ) : (
        <>
          <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead><tr className="bg-[#09090B] text-white text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Data/Ora</th>
                <th className="px-4 py-3 text-left">Utente</th>
                <th className="px-4 py-3 text-left">Azione</th>
                <th className="px-4 py-3 text-left">Entita</th>
                <th className="px-4 py-3 text-left">Path</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {logs.map((log, i) => (
                  <React.Fragment key={i}>
                    <tr className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`audit-row-${i}`}>
                      <td className="px-4 py-3 text-xs font-mono text-zinc-600">{formatTime(log.timestamp)}</td>
                      <td className="px-4 py-3 text-xs">{log.user_email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${ACTION_COLORS[log.action] || "bg-zinc-100 text-zinc-600"}`}>
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">{ENTITY_LABELS[log.entity] || log.entity}</td>
                      <td className="px-4 py-3 text-xs font-mono text-zinc-500 max-w-[260px] truncate">{log.method} {log.path}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${log.status_code < 300 ? "bg-green-100 text-green-700" : log.status_code < 400 ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>
                          {log.status_code}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setExpanded(expanded === i ? null : i)} data-testid={`audit-expand-${i}`}
                          className="text-xs text-[#FF5200] font-bold uppercase">
                          {expanded === i ? "Chiudi" : "Dettagli"}
                        </button>
                      </td>
                    </tr>
                    {expanded === i && (
                      <tr><td colSpan="7" className="px-4 py-3 bg-zinc-50 border-b border-zinc-100">
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div><span className="font-bold uppercase text-zinc-500">IP:</span> <span className="font-mono">{log.ip || "-"}</span></div>
                          <div><span className="font-bold uppercase text-zinc-500">Entity ID:</span> <span className="font-mono">{log.entity_id || "-"}</span></div>
                          <div className="col-span-2">
                            <p className="font-bold uppercase text-zinc-500 mb-1">Body:</p>
                            <pre className="bg-white border border-zinc-200 rounded-sm p-2 font-mono text-[10px] overflow-x-auto max-h-48">{JSON.stringify(log.body, null, 2)}</pre>
                          </div>
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-xs text-zinc-500">
            <p>Totale: <strong>{total}</strong> log</p>
            <div className="flex items-center gap-2">
              <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="audit-prev"
                className="px-3 py-1.5 border border-zinc-200 rounded-sm font-bold uppercase disabled:opacity-30">&larr; Prec</button>
              <span className="px-2">Pag {page + 1} / {totalPages || 1}</span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="audit-next"
                className="px-3 py-1.5 border border-zinc-200 rounded-sm font-bold uppercase disabled:opacity-30">Succ &rarr;</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
